import { PdfToiChecklistItem } from '../models/pdf-toi.model';
import { ToiMidiaItem } from '../models/toi-detalhe.model';
import { extrairNomeArquivoMidiaToi, resolverUrlMidiaToi } from './toi-export-pdf.util';

/** Chave estável para controle de mídias ocultas no PDF. */
export function getToiMidiaKey(midia: ToiMidiaItem, index?: number): string {
  return (
    midia.id ??
    midia.urlThumbnail ??
    midia.downloadUrl ??
    (midia.legenda ? `${midia.legenda}-${index ?? 0}` : `midia-${index ?? 0}`)
  );
}

/** Lê as mídias de `jsonControleFotos`, que pode vir como objeto `{ midias }` ou array de nomes/URLs. */
export function resolverMidiasToi(jsonFotos: unknown): ToiMidiaItem[] | null {
  if (!jsonFotos) {
    return null;
  }

  if (Array.isArray((jsonFotos as { midias?: ToiMidiaItem[] }).midias)) {
    return (jsonFotos as { midias: ToiMidiaItem[] }).midias.map((midia) => normalizarMidiaItem(midia));
  }

  if (Array.isArray(jsonFotos)) {
    return jsonFotos
      .map((item) => mapMidiaControleFotos(item))
      .filter((item): item is ToiMidiaItem => !!item);
  }

  return null;
}

/** Usa o label do checklist ("Foto 1", "Foto 2"...) como legenda da mídia correspondente. */
export function aplicarLegendasDoChecklistToi(
  midias: ToiMidiaItem[] | null,
  checklist: PdfToiChecklistItem[] | undefined | null
): ToiMidiaItem[] | null {
  if (!midias?.length || !checklist?.length) {
    return midias;
  }

  const porArquivo = new Map<string, string>();
  for (const item of checklist) {
    const referencia = item.valor ? item.valor.trim().replace(/[\t\n\r]/g, ' ').replace(/\s+/g, ' ') : '';
    if (!referencia || !/^foto\s/i.test(item.label)) {
      continue;
    }
    const nomeArquivo = extrairNomeArquivoMidiaToi(referencia);
    if (nomeArquivo) {
      porArquivo.set(nomeArquivo, item.label.trim());
    }
  }

  return midias.map((midia) => {
    const nome = extrairNomeArquivoMidia(midia);
    const legendaChecklist = nome ? porArquivo.get(nome) : undefined;
    return legendaChecklist ? { ...midia, id: nome, legenda: legendaChecklist } : midia;
  });
}

export function normalizarMidiasToi(midias: ToiMidiaItem[] | undefined | null): ToiMidiaItem[] {
  if (!midias?.length) {
    return [];
  }

  return midias.map((midia, index) => ({
    ...midia,
    id: getToiMidiaKey(midia, index),
  }));
}

function extrairNomeArquivoMidia(midia: ToiMidiaItem): string | undefined {
  const fromUrl =
    (midia.downloadUrl ? extrairNomeArquivoMidiaToi(midia.downloadUrl) : '') ||
    (midia.urlThumbnail ? extrairNomeArquivoMidiaToi(midia.urlThumbnail) : '');
  if (fromUrl) {
    return fromUrl;
  }
  return midia.legenda || midia.id;
}

function mapMidiaControleFotos(item: unknown): ToiMidiaItem | null {
  if (typeof item === 'string') {
    const referencia = item.trim();
    if (!referencia) {
      return null;
    }

    const url = resolverUrlMidiaToi(referencia);
    return {
      legenda: extrairNomeArquivoMidiaToi(referencia),
      urlThumbnail: url,
      downloadUrl: url,
    };
  }

  const record = item as Partial<ToiMidiaItem>;
  if (!record.legenda && !record.urlThumbnail && !record.downloadUrl) {
    return null;
  }

  return normalizarMidiaItem(record);
}

function normalizarMidiaItem(midia: Partial<ToiMidiaItem>): ToiMidiaItem {
  const downloadUrl = midia.downloadUrl?.trim() || midia.urlThumbnail?.trim() || '';
  const url = downloadUrl ? resolverUrlMidiaToi(downloadUrl) : '';
  const legenda =
    midia.legenda?.trim() ||
    (downloadUrl ? extrairNomeArquivoMidiaToi(downloadUrl) : '') ||
    'Foto TOI';

  return {
    ...midia,
    legenda,
    urlThumbnail: midia.urlThumbnail?.trim() ? resolverUrlMidiaToi(midia.urlThumbnail) : url,
    downloadUrl: downloadUrl ? url : undefined,
  };
}
